// Единый обработчик universal links (iOS) / app links (Android).
//
// Ссылка вида https://web.rentprog.ru/<путь> открывает приложение, ОС отдаёт
// её через @capacitor/app → событие appUrlOpen. Здесь она превращается в
// маршрут роутера: путь + query + hash передаются как есть, чтобы работали
// /bookings/123, /reset_password/<token>, /app-return?stripe_autopay=success.
// Маршрут, которого нет (catch-all '*' → /404), заменяем на /dashboard:
// пользователь по ссылке ждёт приложение, а не страницу ошибки.
//
// Слушатель ставится один раз в main.js рядом с роутером, а не в AppHeader:
// тот не рендерится на signin/signup/forgot/reset, и ссылка без сессии
// обрабатывалась только после логина. Авторизация — забота beforeEach
// (requiresAuth → /signin?from=<путь>, Signin возвращает на from).
//
// Модуль без импорта роутера и Capacitor — для проверяемости, см.
// tests/unit/deepLink.spec.js и обоснование в staffGate.js.

// Хосты в связке с ОС: applinks в ios/App/App/App.entitlements и
// intent-filter'ы в android/app/src/main/AndroidManifest.xml.
export const DEEP_LINK_HOSTS = ["web.rentprog.ru", "web.rentprog.com"];

const FALLBACK_PATH = "/dashboard";
const NOT_FOUND_PATH = "/404";

// URL → строка маршрута ("/path?query#hash") или null, если ссылка не наша.
export function deepLinkTarget(rawUrl) {
  if (typeof rawUrl !== "string") return null;
  let url;
  try {
    url = new URL(rawUrl);
  } catch (e) {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (!DEEP_LINK_HOSTS.includes(url.hostname)) return null;
  return url.pathname + url.search + url.hash;
}

// Ведёт роутер по относительному пути ("/bookings/7?tab=x"). Принимает только
// путь от корня: абсолютные URL и "//host" (protocol-relative) отбрасываются —
// путь может прийти из data пуша, и уводить приложение наружу он не должен.
// Возвращает promise, который не отклоняется: NavigationDuplicated и прочие
// отказы навигации здесь не ошибка.
export function openAppPath(router, path) {
  if (typeof path !== "string" || path.charAt(0) !== "/" || path.charAt(1) === "/") {
    return Promise.resolve();
  }
  // vue-router 3: catch-all '*' с redirect резолвится в route.path === '/404',
  // так что «нет маршрута» = именно этот путь; пустого matched при catch-all не бывает.
  const resolved = router.resolve(path);
  const route = resolved && resolved.route;
  const missing = !route || route.path === NOT_FOUND_PATH;
  const destination = missing ? FALLBACK_PATH : path;
  return Promise.resolve(router.push(destination)).catch(() => {});
}

// Ведёт роутер по полной ссылке universal / app link.
export function openDeepLink(router, rawUrl) {
  const target = deepLinkTarget(rawUrl);
  if (target === null) return Promise.resolve();
  return openAppPath(router, target);
}

// Тап по пушу: ActionPerformed из @capacitor/push-notifications →
// относительный путь из data.url (кладёт бэкенд: PushNotifier,
// NewBookingPushNotifier, ResponsiblePushNotifier) или null.
export function pushTapPath(actionEvent) {
  const data = actionEvent && actionEvent.notification && actionEvent.notification.data;
  const url = data && data.url;
  return typeof url === "string" && url.length > 0 ? url : null;
}

// appPlugin — App из @capacitor/app (или совместимый объект с addListener).
export function installDeepLinkHandler(appPlugin, router) {
  appPlugin.addListener("appUrlOpen", (event) => openDeepLink(router, event && event.url));
}
